// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Import DOCX Dialog
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';
import { BASE, getToken } from '../../../../shared/services/api';

interface ImportDocxDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (content: unknown) => void;
}

export function ImportDocxDialog({ open, onClose, onImport }: ImportDocxDialogProps) {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    titulo: string;
    secciones: number;
    warnings: string[];
  } | null>(null);
  const [parsedContent, setParsedContent] = useState<unknown>(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setFile(null);
    setLoading(false);
    setError(null);
    setResult(null);
    setParsedContent(null);
    setDragOver(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = useCallback(async (f: File) => {
    setFile(f);
    setError(null);
    setResult(null);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', f);

      // Multipart: fetch manual con Bearer (request() forzaría Content-Type JSON
      // e invalidaría el boundary multipart generado por el browser).
      const token = getToken();
      const res = await fetch(`${BASE}/importacion/parsear`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: 'Error al importar' } }));
        throw new Error(err.error?.message ?? `Error ${res.status}`);
      }

      const data = await res.json();
      setParsedContent(data.data?.contenidoJson ?? data.contenidoJson ?? data.data);
      setResult({
        titulo: data.data?.titulo ?? data.titulo ?? f.name,
        secciones: data.data?.secciones ?? data.secciones ?? 0,
        warnings: data.data?.warnings ?? data.warnings ?? [],
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f && f.name.endsWith('.docx')) processFile(f);
      else setError('Solo se aceptan archivos .docx');
    },
    [processFile],
  );

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: '28px 32px',
          maxWidth: 520,
          width: '90%',
          boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1F3864', margin: '0 0 16px' }}>
          {t.editor.importDocx ?? 'Importar DOCX'}
        </h3>

        {!file && !loading && !result && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? '#33BCE1' : '#d1d5db'}`,
              borderRadius: 8,
              padding: '40px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
              background: dragOver ? '#F0FBFF' : '#FAFAFA',
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="1.5"
              style={{ margin: '0 auto 12px' }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p style={{ color: '#6B7280', fontSize: 13, margin: 0 }}>
              {t.editor.dragDocx ?? 'Arrastra un archivo .docx o haz clic para seleccionar'}
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".docx"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) processFile(f);
              }}
            />
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div className="via-thinking" style={{ width: 32, height: 32, margin: '0 auto 12px' }}>
              <img
                src="/img/Imagotipo-Color.png"
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <p style={{ color: '#6B7280', fontSize: 13 }}>
              {t.editor.processingDocx ?? 'Procesando...'} {file?.name}
            </p>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: 16,
              background: '#FEF2F2',
              borderRadius: 8,
              color: '#DC2626',
              fontSize: 13,
              marginTop: 12,
            }}
          >
            {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: 12 }}>
            <div style={{ padding: 16, background: '#F0FDF4', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#15803D' }}>{result.titulo}</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                {result.secciones} secciones detectadas
              </div>
              {result.warnings.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#92400E' }}>
                  {result.warnings.map((w, i) => (
                    <div key={i}>- {w}</div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '7px 16px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  border: '1px solid #E5E7EB',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t.common.cancelar}
              </button>
              <button
                onClick={() => {
                  if (parsedContent) {
                    onImport(parsedContent);
                    handleClose();
                  }
                }}
                style={{
                  padding: '7px 16px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  border: 'none',
                  background: '#1F3864',
                  color: 'white',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t.editor.insertContent ?? 'Insertar contenido'}
              </button>
            </div>
          </div>
        )}

        {!result && !loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              onClick={handleClose}
              style={{
                padding: '7px 16px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                border: '1px solid #E5E7EB',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t.common.cancelar}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
